import { memo, useEffect, useLayoutEffect, useRef, useState } from "react";
import type { Item, Note, Person, TripView } from "./api.ts";
import { examplePrompt } from "./ask-ai.ts";
import { faces, Picks } from "./Details.tsx";
import { itemKind, kindClass, kindOf, km, originLabel, waitingSuggestion } from "./format.ts";
import { distanceM, type LngLat, point } from "./geo.ts";
import { HOVERS, type Hovered, itemAt } from "./hover.ts";
import { useFolds, wholeIn } from "./kept.ts";
import { NEARBY_M } from "./map/common.ts";
import { type ListGroup, type ListLayout, listGroups } from "./maplist.ts";
import { AddNote, NoteList } from "./Notes.tsx";
import { useReadOnly } from "./readOnly.ts";
import { keptKey } from "./reopen.ts";
import { AvatarStack, Icon } from "./souvenir.ts";
import { viewPrompt } from "./Tips.tsx";
import { Count, Empty, ItemIcon, KindIcon, SectionTitle } from "./ui.tsx";

// The map's list: beside the map on a computer, over it on a phone (features/map.md). A search at its
// top narrows it and the map; what's on the map is grouped under the stay it's nearest to (else by
// kind), each group folding; then what isn't on the map yet. On a computer it stays while things are
// looked at: the selected one's row lit like its pin, and hovering a row lights its pin (hover.ts).

export function Overview({
  trip,
  items,
  anything,
  layout,
  many,
  filtered,
  query,
  onQuery,
  onQueryDone,
  notes,
  me,
  selected = null,
  hover = null,
  onSelect,
  onChanged,
}: {
  trip: TripView;
  // What the filters and the search show.
  items: Item[];
  // Whether the trip has anything on the map's list at all, whatever the filters and search say.
  anything: boolean;
  // How what's on the map is grouped and ordered (maplist.ts), from everything on it.
  layout: ListLayout;
  // More than enough places to fold all but the first group, as the filters show them.
  many: boolean;
  // Whether the What filter is hiding anything.
  filtered: boolean;
  query: string;
  onQuery: (query: string) => void;
  // Enter: done typing, so the map shows the matches at once.
  onQueryDone: (query: string) => void;
  notes: Note[];
  // Where I am, while I'm looking: what's within reach comes first.
  me: LngLat | null;
  // What's selected, its row lit (beside the map on a computer, over it on a phone, the place in brief under
  // it); and on a computer, what the pointer is over, here or on the map.
  selected?: string | null;
  hover?: Hovered | null;
  onSelect: (id: string) => void;
  onChanged: () => void;
}) {
  const searching = query.trim() !== "";
  // A read-only trip: its notes only when there are some (nothing can be added).
  const readOnly = useReadOnly();
  const placed = items.filter((i) => point(i));
  const near = me
    ? placed
        .map((i) => ({ i, d: distanceM(me, point(i) as LngLat) }))
        .filter(({ d }) => d <= NEARBY_M)
        .sort((a, b) => a.d - b.d)
    : [];
  const unplaced = items.filter((i) => !point(i));
  const groups = listGroups(layout, new Set(placed.map((i) => i.id)));
  // Folded or open, as I left each on this device; past 30 places all but the first start folded. While
  // searching, every group with a match is open, and what I fold then is forgotten with the search.
  const folds = useFolds(keptKey("folds", trip.you, trip.id));
  const [searchFolds, setSearchFolds] = useState<{ query: string; folded: Record<string, boolean> }>({
    query,
    folded: {},
  });
  const isFolded = (key: string, n: number) =>
    searching
      ? searchFolds.query === query && (searchFolds.folded[key] ?? false)
      : folds.folded(`list:${key}`, many && n > 0);
  const fold = (key: string, folded: boolean) => {
    if (!searching) return folds.set(`list:${key}`, folded);
    setSearchFolds((s) => ({ query, folded: { ...(s.query === query ? s.folded : {}), [key]: folded } }));
  };
  const members = trip.members;
  const root = useRef<HTMLDivElement>(null);

  // Something newly selected (a pin clicked, Show on map, Back) shows in the list: its group opens, as
  // if I'd opened it (kept like any fold), and its row scrolls into view, the rest of the list where it
  // was. Once each time something else is selected, so its group can still be folded again.
  const opened = useRef<string | null>(null);
  useLayoutEffect(() => {
    if (selected === opened.current) return;
    opened.current = selected;
    groups.forEach((g, n) => {
      if (g.items.some((i) => i.id === selected) && isFolded(g.key, n)) fold(g.key, false);
    });
  });
  // What the list showed with is already in view (KeptPanel puts it there before the list paints): only a
  // list already showing scrolls, smoothly, to something newly selected (a pin clicked beside it).
  const scrolled = useRef<string | null>(selected);
  useEffect(() => {
    if (selected === scrolled.current) return;
    const row = selected ? root.current?.querySelector(`.tm-row[data-item="${CSS.escape(selected)}"]`) : null;
    // Its group opening first: next time.
    if (selected && !row) return;
    scrolled.current = selected;
    // Already in view, clear of the search stuck at the top: the list stays as it is. Else to the middle,
    // with what's around it. The panel beside the map on a computer, the list over it on a phone.
    const list = row?.closest<HTMLElement>(".tm-panel, .tm-layer-body");
    if (!row || !list || wholeIn(list, row, ".tm-list-search")) return;
    const still = matchMedia("(prefers-reduced-motion: reduce)").matches;
    row.scrollIntoView({ block: "center", behavior: still ? "auto" : "smooth" });
  });

  // Hovering a row lights its pin, and hovering a pin lights its row (hover.ts): marked on the row itself,
  // so no row is drawn again as the pointer sweeps down the list.
  useEffect(() => {
    const el = root.current;
    if (!el || !hover) return;
    let lit: Element[] = [];
    const light = (id: string | null) => {
      for (const row of lit) row.removeAttribute("data-hover");
      lit = id ? [...el.querySelectorAll(`.tm-row[data-item="${CSS.escape(id)}"]`)] : [];
      for (const row of lit) row.setAttribute("data-hover", "");
    };
    const over = (e: PointerEvent) => {
      if (hover.hovers(e)) hover.set(itemAt(e, el, ".tm-row[data-item]"), "list");
    };
    const leave = () => hover.leave("list");
    // On the list at all, its whole panel (padding and all, so its edge isn't a way off it): when the
    // pointer leaves it, the map comes back from a hover's zoom-out (TripMap).
    const panel = el.closest(".tm-panel") ?? el;
    const enterPanel = (e: PointerEvent) => hover.hovers(e) && hover.list(true);
    const leavePanel = () => hover.list(false);
    light(hover.id);
    const off = hover.subscribe(light);
    el.addEventListener("pointerover", over);
    el.addEventListener("pointerleave", leave);
    panel.addEventListener("pointerenter", enterPanel as EventListener);
    panel.addEventListener("pointerleave", leavePanel);
    // Already under the pointer as it shows (the full card closed back to the list).
    if (panel.matches(":hover") && matchMedia(HOVERS).matches) hover.list(true);
    return () => {
      off();
      el.removeEventListener("pointerover", over);
      el.removeEventListener("pointerleave", leave);
      panel.removeEventListener("pointerenter", enterPanel as EventListener);
      panel.removeEventListener("pointerleave", leavePanel);
      hover.leave("list");
      hover.list(false);
      light(null);
    };
  }, [hover]);

  if (!anything) {
    return (
      <div className="tm-overview" ref={root}>
        <h1 className="display-m">{readOnly ? "Nothing here" : "Nothing here yet"}</h1>
        <Empty prompt={viewPrompt("map", trip.title)} readOnly="Nothing in the trip.">
          Places, stays and journeys show up here and on the map as your AI adds them, with who wants each.
        </Empty>
      </div>
    );
  }
  return (
    // Something selected: its row is waited for as the list shows (KeptPanel, kept.ts).
    <div className="tm-overview" ref={root} data-selected={selected ?? undefined}>
      <SearchBox value={query} onChange={onQuery} onDone={onQueryDone} />
      <span className="tm-hidden" aria-live="polite">
        {searching ? `${items.length} ${items.length === 1 ? "match" : "matches"}` : ""}
      </span>
      {me && (
        <section aria-labelledby="near-you">
          <SectionTitle id="near-you" count={near.length}>
            Near you
          </SectionTitle>
          {near.length === 0 ? (
            <Empty>Nothing in the trip within {km(NEARBY_M)} of you.</Empty>
          ) : (
            <ul className="tm-rows">
              {near.map(({ i, d }) => (
                <Row key={i.id} item={i} members={members} onSelect={onSelect} distance={d} lit={i.id === selected} />
              ))}
            </ul>
          )}
        </section>
      )}
      {/* The trip's notes aren't what a search looks for: out of the way while searching. */}
      {!searching && (!readOnly || notes.length > 0) && (
        <section className="tm-section" aria-labelledby="trip-notes">
          <SectionTitle id="trip-notes" count={notes.length}>
            Notes on the trip
          </SectionTitle>
          {notes.length > 0 && (
            <NoteList notes={notes} members={members} you={trip.you} edit={{ trip: trip.id, onChanged }} />
          )}
          <AddNote on={trip.id} edit={{ trip: trip.id, onChanged }} />
        </section>
      )}
      {searching && items.length === 0 && (
        <Empty>
          Nothing {filtered ? "the filters show " : "in the trip "}matches “{query.trim()}”.
        </Empty>
      )}
      {(!searching || placed.length > 0) && (
        <section aria-labelledby="on-map">
          <SectionTitle id="on-map" count={placed.length}>
            On the map
          </SectionTitle>
          {placed.length === 0 ? (
            <Empty prompt={examplePrompt(trip.title, "mapPlaces")} readOnly="Nothing on the map.">
              Nothing on the map yet. Things show here once your AI knows where they are.
            </Empty>
          ) : (
            <div className="tm-list-groups">
              {groups.map((g, n) => (
                <Group
                  key={g.key}
                  group={g}
                  folded={isFolded(g.key, n)}
                  onFold={(folded) => fold(g.key, folded)}
                  members={members}
                  selected={selected}
                  onSelect={onSelect}
                />
              ))}
            </div>
          )}
        </section>
      )}
      {unplaced.length > 0 && (
        <section aria-labelledby="not-on-map">
          <SectionTitle id="not-on-map" count={unplaced.length}>
            {readOnly ? "Not on the map" : "Not on the map yet"}
          </SectionTitle>
          <p className="caption tm-muted">
            {readOnly
              ? "No coordinates."
              : "No coordinates yet. Tripmo asks your AI to look them up when it next opens the trip, or ask it now."}
          </p>
          <ul className="tm-rows">
            {unplaced.map((i) => (
              <Row key={i.id} item={i} members={members} onSelect={onSelect} lit={i.id === selected} />
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

// The search at the list's top. What's typed shows at once; the list and the map follow as soon as
// they can (the caller narrows them in a transition), so typing stays smooth with hundreds of places.
// Its x, or Escape, clears it; Enter puts the phone's keyboard away to show what it found, and the map
// shows the matches without waiting for the pause.
function SearchBox({
  value,
  onChange,
  onDone,
}: {
  value: string;
  onChange: (query: string) => void;
  onDone: (query: string) => void;
}) {
  const [text, setText] = useState(value);
  const input = useRef<HTMLInputElement>(null);
  const change = (q: string) => {
    setText(q);
    onChange(q);
  };
  return (
    <search className="tm-list-search">
      <div className="tm-search">
        <ItemIcon name="search" size={20} />
        <input
          ref={input}
          type="search"
          className="body"
          enterKeyHint="search"
          autoComplete="off"
          spellCheck={false}
          placeholder="Search the trip"
          aria-label="Search the trip"
          value={text}
          onChange={(e) => change(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Escape" && text !== "") {
              // Clearing it, before the page's own Escape (which closes the list on a phone).
              e.preventDefault();
              e.stopPropagation();
              change("");
            } else if (e.key === "Enter") {
              e.currentTarget.blur();
              onDone(text);
            }
          }}
        />
        {text !== "" && (
          <button
            type="button"
            className="tm-search-clear"
            aria-label="Clear the search"
            onClick={() => {
              change("");
              input.current?.focus();
            }}
          >
            <Icon name="close" size={16} />
          </button>
        )}
      </div>
    </search>
  );
}

// A group of the list: its head (where we stay, its nights, how many) folds and unfolds its rows, like
// a kind on Bookings.
function Group({
  group,
  folded,
  onFold,
  members,
  selected,
  onSelect,
}: {
  group: ListGroup;
  folded: boolean;
  onFold: (folded: boolean) => void;
  members: Person[];
  selected: string | null;
  onSelect: (id: string) => void;
}) {
  const id = `list-${group.key.replace(/[^\w-]/g, "-")}`;
  return (
    <section className={`tm-choice tm-list-group${folded ? " is-folded" : ""}`} aria-labelledby={`${id}-title`}>
      <h3 className="tm-choice-heading">
        <button
          type="button"
          className="tm-choice-head tm-list-group-head"
          aria-expanded={!folded}
          aria-controls={`${id}-rows`}
          // Read out with a pause between how many and the nights, not "23 1 to 5 Dec".
          aria-label={[`${group.label} ${group.items.length}`, group.caption].filter(Boolean).join(", ")}
          onClick={() => onFold(!folded)}
        >
          <span className="tm-choice-title">
            <span id={`${id}-title`} className="body-strong">
              {group.label}
              <Count n={group.items.length} />
            </span>
            {group.caption && <span className="caption tm-muted">{group.caption}</span>}
          </span>
          <Icon name="chevron" size={18} className="tm-choice-caret" />
        </button>
      </h3>
      {!folded && (
        <ul id={`${id}-rows`} className="tm-rows">
          {group.entries.map((e) =>
            "item" in e ? (
              <Row key={e.item.id} item={e.item} members={members} onSelect={onSelect} lit={e.item.id === selected} />
            ) : (
              <ChoiceRows
                key={e.choice.id}
                choice={e.choice}
                options={e.options}
                members={members}
                selected={selected}
                onSelect={onSelect}
              />
            ),
          )}
        </ul>
      )}
    </section>
  );
}

// A choice's open options, together under its title with how many ("Osaka: where we sleep · 3
// options"), each row an option.
function ChoiceRows({
  choice,
  options,
  members,
  selected,
  onSelect,
}: {
  choice: Item;
  options: Item[];
  members: Person[];
  selected: string | null;
  onSelect: (id: string) => void;
}) {
  const id = `list-choice-${choice.id}`;
  return (
    <li className="tm-list-choice">
      <p id={id} className="caption tm-list-choice-title">
        <span>{choice.title}</span>
        <span className="tm-muted">
          {" · "}
          {options.length} {options.length === 1 ? "option" : "options"}
        </span>
      </p>
      <ul className="tm-rows" aria-labelledby={id}>
        {options.map((o) => (
          <Row key={o.id} item={o} members={members} onSelect={onSelect} lit={o.id === selected} />
        ))}
      </ul>
    </li>
  );
}

// One thing in the list. Drawn again only when it changes, not as the search narrows the rest. A
// choice's option is drawn as one, as on the map: its icon ringed with a dashed line, and "Option".
// Selected, it's lit like its pin: on its kind's colour, its icon ringed in the sun. Which item it is
// (`data-item`) tells the pointer's hover what to light.
const Row = memo(function Row({
  item,
  members,
  onSelect,
  distance,
  lit = false,
}: {
  item: Item;
  members: Person[];
  onSelect: (id: string) => void;
  // How far it is from me, when that's what the list is about.
  distance?: number;
  lit?: boolean;
}) {
  const shown = faces(item, members);
  const suggested = waitingSuggestion(item, members);
  const option = item.status === "option";
  return (
    <li>
      <button
        type="button"
        className={`tm-row ${kindClass(kindOf(item))}${lit ? " is-lit" : ""}`}
        data-item={item.id}
        aria-current={lit ? "true" : undefined}
        onClick={() => onSelect(item.id)}
      >
        <KindIcon item={item} suggested={suggested} option={option} />
        <span className="tm-row-text">
          <span className="callout">{item.title}</span>
          <span className="caption tm-muted">
            {[
              distance !== undefined && km(distance),
              option && "Option",
              suggested && originLabel(item),
              itemKind(item),
            ]
              .filter(Boolean)
              .join(" · ")}
            <Picks item={item} />
          </span>
        </span>
        {shown.length > 0 && <AvatarStack people={shown} size={24} max={3} />}
      </button>
    </li>
  );
});
